import { isApiError, unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, EMAIL_MAX_LENGTH, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input, toast } from "@sagot/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { zodResolver } from "../lib/zod-resolver";
import { useApi } from "../session/session-provider";
import { SECONDS_PER_MINUTE, WORK_EMAIL_PLACEHOLDER } from "./auth-constants";
import { AuthLayout } from "./components/auth-layout";
import { useCreateAccountPrompt } from "./components/auth-prompts";
import { FormAlert } from "./components/form-alert";

function useForgotSchema() {
  const { t } = useTranslation();
  return z.object({ email: z.string().trim().min(1, t("auth.common.emailRequired")).max(EMAIL_MAX_LENGTH, t("auth.common.emailTooLong")).email(t("auth.common.emailInvalid")) });
}

/** The message for a failed request: the API's Retry-After becomes "try again in about N min" when the reset limit is hit. */
function useForgotMessage(): (error: unknown) => string {
  const { t } = useTranslation();
  const fallback = useErrorMessage();
  return (error) => {
    if (isApiError(error) && error.code === "rate_limited" && error.retryAfter !== undefined) {
      return t("auth.forgot.throttled", { minutes: Math.max(1, Math.ceil(error.retryAfter / SECONDS_PER_MINUTE)) });
    }
    return fallback(error);
  };
}

interface SuccessPanelProps {
  resending: boolean;
  resendError: string | null;
  onResend: () => void;
  onDismissError: () => void;
}

function SuccessPanel({ resending, resendError, onResend, onDismissError }: SuccessPanelProps) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="fp-success-title">
      <span className="a-state" data-tone="ok">
        <Icon name="check" />
      </span>
      <h1 id="fp-success-title" className="a-title">
        {t("auth.forgot.successHeading")}
      </h1>
      <p className="a-sub">{t("auth.forgot.sentMessage")}</p>
      {resendError ? <FormAlert title={resendError} onDismiss={onDismissError} /> : null}
      <Button asChild variant="secondary" size="lg" className="w-full">
        <AppLink href={CONSOLE_PATHS.login}>{t("auth.common.backToSignIn")}</AppLink>
      </Button>
      <p className="a-note">
        {t("auth.forgot.noteLead")}{" "}
        <button type="button" disabled={resending} onClick={onResend}>
          {t("auth.forgot.sendAgain")}
        </button>
        {t("auth.forgot.noteTail")}
      </p>
    </section>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const api = useApi();
  const prompt = useCreateAccountPrompt();
  const message = useForgotMessage();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<{ email: string }>({ resolver: zodResolver(useForgotSchema()), defaultValues: { email: "" } });

  const send = async (email: string) => {
    await unwrap(api.POST("/v1/auth/forgot", { body: { email } }));
  };
  // Same outcome whether or not the address has an account: the API answers 202 either way, and so does this page.
  const onSubmit = handleSubmit(async ({ email }) => {
    setError(null);
    try {
      await send(email);
      setSentTo(email);
    } catch (caught) {
      setError(message(caught));
    }
  });
  const resend = async () => {
    if (!sentTo) return;
    setResending(true);
    setResendError(null);
    try {
      await send(sentTo);
      toast.success(t("auth.forgot.sentAgain"));
    } catch (caught) {
      setResendError(message(caught));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout title={t("auth.forgot.title", { product: PRODUCT_NAME })} prompt={prompt}>
      {sentTo ? (
        <SuccessPanel
          resending={resending}
          resendError={resendError}
          onResend={() => void resend()}
          onDismissError={() => {
            setResendError(null);
          }}
        />
      ) : (
        <section aria-labelledby="fp-title">
          <h1 id="fp-title" className="a-title">
            {t("auth.forgot.heading")}
          </h1>
          <p className="a-sub">{t("auth.forgot.subtitle")}</p>
          {error ? (
            <FormAlert
              title={error}
              onDismiss={() => {
                setError(null);
              }}
            />
          ) : null}
          <form noValidate onSubmit={(event) => void onSubmit(event)}>
            <Field className="a-fld" label={t("auth.common.workEmail")} error={formState.errors.email?.message}>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus -- single-field page; matches the mockup's autofocus */}
              <Input type="email" autoComplete="email" inputMode="email" placeholder={WORK_EMAIL_PLACEHOLDER} autoFocus {...register("email")} />
            </Field>
            <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
              {t("auth.forgot.submit")}
            </Button>
          </form>
          <p className="a-foot">
            <AppLink href={CONSOLE_PATHS.login} className="a-link">
              {t("auth.common.backToSignIn")}
            </AppLink>
          </p>
        </section>
      )}
    </AuthLayout>
  );
}
